import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const MandateFormModal = ({ mandate, centers, onClose, onSave }) => {
  const [form, setForm] = useState({
    center_id: mandate?.center_id || '',
    owner_name: mandate?.owner_name || '',
    commission_percentage: mandate?.commission_percentage || '',
    fixed_fee_amount: mandate?.fixed_fee_amount || 0,
    agreement_value: mandate?.agreement_value || 0,
    mandate_start_date: mandate?.mandate_start_date || '',
    mandate_expiry_date: mandate?.mandate_expiry_date || '',
    status: mandate?.status || 'active',
    is_exclusive: mandate?.is_exclusive || false,
    notes: mandate?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const commissionValue = form?.agreement_value > 0 && form?.commission_percentage > 0
    ? (parseFloat(form?.agreement_value) * parseFloat(form?.commission_percentage) / 100)?.toFixed(0)
    : 0;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await onSave(mandate?.id, {
        ...form,
        commission_percentage: parseFloat(form?.commission_percentage) || 0,
        fixed_fee_amount: parseFloat(form?.fixed_fee_amount) || 0,
        agreement_value: parseFloat(form?.agreement_value) || 0,
      });
      onClose();
    } catch (err) {
      alert(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">
            {mandate?.id ? 'Edit Mandate' : 'New Mandate'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Center *</label>
            <select
              value={form?.center_id}
              onChange={e => setForm(f => ({ ...f, center_id: e?.target?.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select center</option>
              {centers?.map(c => <option key={c?.id} value={c?.id}>{c?.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name *</label>
            <input
              type="text"
              value={form?.owner_name}
              onChange={e => setForm(f => ({ ...f, owner_name: e?.target?.value }))}
              required
              placeholder="Property owner name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commission % *</label>
            <input
              type="number"
              value={form?.commission_percentage}
              onChange={e => setForm(f => ({ ...f, commission_percentage: e?.target?.value }))}
              required
              min="0" max="100" step="0.1"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Fee (₹)</label>
            <input
              type="number"
              value={form?.fixed_fee_amount}
              onChange={e => setForm(f => ({ ...f, fixed_fee_amount: e?.target?.value }))}
              min="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agreement Value (₹)</label>
            <input
              type="number"
              value={form?.agreement_value}
              onChange={e => setForm(f => ({ ...f, agreement_value: e?.target?.value }))}
              min="0"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-end">
            <div className="w-full bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <p className="text-xs text-blue-600 font-medium">Auto-Calculated Commission Value</p>
              <p className="text-lg font-bold text-blue-700">₹{parseInt(commissionValue)?.toLocaleString('en-IN')}</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
            <input
              type="date"
              value={form?.mandate_start_date}
              onChange={e => setForm(f => ({ ...f, mandate_start_date: e?.target?.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
            <input
              type="date"
              value={form?.mandate_expiry_date}
              onChange={e => setForm(f => ({ ...f, mandate_expiry_date: e?.target?.value }))}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={form?.status}
              onChange={e => setForm(f => ({ ...f, status: e?.target?.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="active">Active</option>
              <option value="expiring">Expiring</option>
              <option value="expired">Expired</option>
              <option value="pending_renewal">Pending Renewal</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="is_exclusive"
              checked={form?.is_exclusive}
              onChange={e => setForm(f => ({ ...f, is_exclusive: e?.target?.checked }))}
              className="w-4 h-4 text-primary-600"
            />
            <label htmlFor="is_exclusive" className="text-sm font-medium text-gray-700">Exclusive Mandate</label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form?.notes}
              onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div className="md:col-span-2 flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Saving...' : 'Save Mandate'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  expiring: 'bg-orange-100 text-orange-700',
  expired: 'bg-red-100 text-red-700',
  pending_renewal: 'bg-yellow-100 text-yellow-700',
  cancelled: 'bg-gray-100 text-gray-600',
};

const MandatesEnhanced = () => {
  const { userProfile } = useAuth();
  const [mandates, setMandates] = useState([]);
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editMandate, setEditMandate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const canEdit = ['super_admin', 'admin']?.includes(userProfile?.role);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [mandatesRes, centersRes] = await Promise.all([
        supabase?.from('mandates')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('centers')?.select('id, name, city')?.eq('is_active', true),
      ]);
      if (mandatesRes?.error) throw mandatesRes?.error;
      setMandates(mandatesRes?.data || []);
      setCenters(centersRes?.data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load mandates');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (id, form) => {
    if (id) {
      const { error } = await supabase?.from('mandates')?.update(form)?.eq('id', id);
      if (error) throw error;
    } else {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error } = await supabase?.from('mandates')?.insert({ ...form, created_by: user?.id });
      if (error) throw error;
    }
    await loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mandate?')) return;
    const { error } = await supabase?.from('mandates')?.delete()?.eq('id', id);
    if (error) { alert(error?.message); return; }
    await loadData();
  };

  const filtered = mandates?.filter(m => {
    const matchSearch = !searchTerm ||
      m?.owner_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      m?.notes?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchStatus = statusFilter === 'all' || m?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = {
    total: mandates?.length,
    active: mandates?.filter(m => m?.status === 'active')?.length,
    expiring: mandates?.filter(m => m?.status === 'expiring')?.length,
    exclusive: mandates?.filter(m => m?.is_exclusive)?.length,
    totalCommission: mandates?.reduce((sum, m) => sum + (m?.commission_value || m?.commission_amount || 0), 0),
  };

  const getCenterName = (centerId) => centers?.find(c => c?.id === centerId)?.name || centerId;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mandate Management</h1>
          <p className="text-gray-600 mt-1">Track property mandates, owner commissions, and exclusivity</p>
        </div>
        {canEdit && (
          <Button onClick={() => { setEditMandate(null); setShowForm(true); }} className="flex items-center gap-2">
            <Icon name="Plus" size={16} />
            New Mandate
          </Button>
        )}
      </div>
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats?.total, icon: 'FileText', color: 'bg-blue-500' },
          { label: 'Active', value: stats?.active, icon: 'CheckCircle', color: 'bg-green-500' },
          { label: 'Expiring', value: stats?.expiring, icon: 'AlertTriangle', color: 'bg-orange-500' },
          { label: 'Exclusive', value: stats?.exclusive, icon: 'Star', color: 'bg-purple-500' },
          { label: 'Total Commission', value: `₹${(stats?.totalCommission / 100000)?.toFixed(1)}L`, icon: 'IndianRupee', color: 'bg-emerald-500' },
        ]?.map(s => (
          <div key={s?.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${s?.color} flex items-center justify-center flex-shrink-0`}>
              <Icon name={s?.icon} size={18} color="white" />
            </div>
            <div>
              <p className="text-xl font-bold text-gray-900">{s?.value}</p>
              <p className="text-xs text-gray-500">{s?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-4">
        <div className="flex-1 min-w-48">
          <input
            type="text"
            placeholder="Search by owner name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e?.target?.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e?.target?.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring</option>
          <option value="expired">Expired</option>
          <option value="pending_renewal">Pending Renewal</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Center</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Owner</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Commission</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Commission Value</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fixed Fee</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Expiry</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Type</th>
                {canEdit && <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered?.length === 0 ? (
                <tr>
                  <td colSpan={canEdit ? 9 : 8} className="text-center py-12 text-gray-500">
                    <Icon name="FileText" className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    No mandates found
                  </td>
                </tr>
              ) : filtered?.map(m => (
                <tr key={m?.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{getCenterName(m?.center_id)}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{m?.owner_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{m?.commission_percentage}%</td>
                  <td className="px-4 py-3 text-sm font-semibold text-emerald-700">
                    ₹{parseInt(m?.commission_value || m?.commission_amount || 0)?.toLocaleString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {m?.fixed_fee_amount > 0 ? `₹${parseInt(m?.fixed_fee_amount)?.toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {m?.mandate_expiry_date ? new Date(m.mandate_expiry_date)?.toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS?.[m?.status] || 'bg-gray-100 text-gray-600'}`}>
                      {m?.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {m?.is_exclusive ? (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">Exclusive</span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">Non-Exclusive</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setEditMandate(m); setShowForm(true); }}
                          className="text-gray-400 hover:text-gray-600">
                          <Icon name="Pencil" size={16} />
                        </button>
                        <button onClick={() => handleDelete(m?.id)}
                          className="text-red-400 hover:text-red-600">
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showForm && (
        <MandateFormModal
          mandate={editMandate}
          centers={centers}
          onClose={() => { setShowForm(false); setEditMandate(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default MandatesEnhanced;
